import * as vp from 'vopay';
import * as Yup from 'yup';

import { FunctionComponent, useEffect, useState } from 'react';
import { Formik } from 'formik';
import { PartnerAccountTransferHandlers } from './types';
import { useBuildPartnerAccountTransfer } from './buildPartnerAccountTransfer';
import { useTranslation } from 'react-i18next';
import { usePartnerData } from '@/providers/PartnerData.provider';
import useFetchPartnerAccounts from '@/hooks/useApi/useFetchPartnerAccounts';

const { Box, Button, Select, TextField, Title, SubTitle, Table, TextArea } = vp;

type AccountTransferProps = {
  isReview: boolean;
  defaultValues: vp.PartnerAccountTransferRequest;
  handlers: PartnerAccountTransferHandlers;
};

type FC = FunctionComponent<AccountTransferProps>;

const PartnerAccountTransfer: FC = (props: AccountTransferProps) => {
  const { isReview, defaultValues, handlers } = props;
  const { t } = useTranslation('translations', { keyPrefix: 'PartnerAccountTransfer' });
  const {
    response: { isRoleViewOnly },
  } = usePartnerData();

  const transferFundOptionsLookup: { [key: string]: string } = {
    Transfer: t('transferTypeTransfer', { defaultValue: 'Transfer' }),
    FundAndTransfer: t('transferTypeFundAndTransfer', { defaultValue: 'Fund and Transfer' }),
    TransferAndWithdraw: t('transferTypeTransferAndWithdraw', { defaultValue: 'Transfer and Withdraw' }),
    FundTransferAndWithdraw: t('transferTypeFundTransferAndWithdraw', { defaultValue: 'Fund, Transfer and Withdraw' }),
  };

  // Fetch accounts for debitor (from) and recipient (to)
  const [fromAccountsResponse, refreshFromAccounts] = useFetchPartnerAccounts({});
  const [toAccountsResponse, refreshToAccounts] = useFetchPartnerAccounts({});

  const [fromAccountDetails, setFromAccountDetails] = useState<{ [key: string]: string }>({});
  const [toAccountDetails, setToAccountDetails] = useState<{ [key: string]: string }>({});

  // Fetch accounts on mount
  useEffect(() => {
    refreshFromAccounts({
      VopayAccountStatus: 'Active',
    });
    refreshToAccounts({
      VopayAccountStatus: 'Active',
    });
  }, []);

  // Build from account details with balance information
  useEffect(() => {
    if (fromAccountsResponse?.balances) {
      const fromAccountsMap: { [key: string]: string } = {};

      Object.values(fromAccountsResponse.balances).forEach((account) => {
        fromAccountsMap[`${account.AccountID}-${account.Currency}-Account`] = t('accountBalanceFormat', {
          accountName: account.AccountName,
          currency: account.Currency,
          balance: account.AvailableBalance,
          defaultValue: '{{accountName}} | Available Balance: {{currency}} ${{balance}}',
        });
      });

      setFromAccountDetails(fromAccountsMap);
    }
  }, [fromAccountsResponse]);

  // Build to account details
  useEffect(() => {
    if (toAccountsResponse?.balances) {
      const toAccountsMap: { [key: string]: string } = {};

      Object.values(toAccountsResponse.balances).forEach((account) => {
        toAccountsMap[account.AccountID] = t('accountBalanceFormatShort', {
          accountName: account.AccountName,
          currency: account.Currency,
          balance: account.AvailableBalance,
          defaultValue: '{{accountName}} | Balance: {{currency}} ${{balance}}',
        });
      });

      setToAccountDetails(toAccountsMap);
    }
  }, [toAccountsResponse]);

  const handleFromAccountSearch = (searchValue: string) => {
    refreshFromAccounts({ SearchText: searchValue, VopayAccountStatus: 'Active' });
  };

  const handleToAccountSearch = (searchValue: string) => {
    refreshToAccounts({ SearchText: searchValue, VopayAccountStatus: 'Active' });
  };

  const accountTransferRows = useBuildPartnerAccountTransfer(
    fromAccountDetails,
    toAccountDetails,
    isReview,
    handleFromAccountSearch,
    handleToAccountSearch,
  );

  return (
    <Formik
      initialValues={defaultValues}
      onSubmit={isReview ? handlers.handleSubmit : handlers.handleReview}
      validationSchema={createdValidationSchema()}
      validateOnChange
      validateOnBlur
      enableReinitialize
    >
      {({ values, touched, errors, handleChange, handleBlur, handleSubmit, handleReset }) => {
        const accountTransferRowsTable: vp.TableProps = {
          name: 'add-account-transfer-fields',
          variant: 'pair',
          columns: {
            Field: { header: 'Labels' },
            Input: { header: 'Fields' },
          },
          rows: accountTransferRows
            .filter((x) => x.visible())
            .map(({ label, id, require, placeholder, preset, type, disabled, getSelectOptions, searchCallback }) => ({
              Field: (
                <Box jc="fs" flx="col">
                  <Box ai="center">
                    {label} {getRequiredIcon(require)}
                  </Box>
                </Box>
              ),
              Input: getField(id, placeholder, preset, type, disabled, getSelectOptions, searchCallback),
            })),
        };

        if (isReview) {
          return (
            <form onSubmit={handleSubmit}>
              <Box flx="col">
                <Title title={t('Review.pageTitle', { defaultValue: 'Account Transfer' })} />
                <br />
                <SubTitle subTitle={t('Review.pageSubTitle', { defaultValue: 'Review' })} />
                <Box flx="col" width="50%" mt="md">
                  <Box mb="md">
                    <Table {...getReviewTableProps(values)} />
                  </Box>
                </Box>
                <Box gap="sm" jc="fe">
                  <Button
                    id="account-transfer-back-btn"
                    color="secondary"
                    label={t('Review.backButtonLabel', { defaultValue: 'Previous' })}
                    isDisabled={isRoleViewOnly}
                    onClick={() => {
                      handlers.handleBack(values);
                    }}
                  />
                  <Button
                    id="account-transfer-save-btn"
                    label={t('Review.submitButtonLabel', { defaultValue: 'Submit' })}
                    type="submit"
                    isDisabled={isRoleViewOnly}
                  />
                </Box>
              </Box>
            </form>
          );
        }

        return (
          <form onSubmit={handleSubmit} onReset={handleReset}>
            <Box mt="sm" mb="sm" jc="sb">
              <Title title={t('pageTitle', { defaultValue: 'Account Transfer' })} />
            </Box>
            <Table {...accountTransferRowsTable} />

            <Box gap="sm" jc="fe">
              <Box width="100%" jc="fe" mt="lg">
                <Button
                  id="partner-account-transfer-review"
                  type="submit"
                  isDisabled={isRoleViewOnly}
                  label={t('submitButton', { defaultValue: 'Review' })}
                />
              </Box>
            </Box>
          </form>
        );

        function getField(
          id: vp.PartnerAccountTransferId,
          placeholder: string | undefined,
          preset: keyof vp.TextFieldPresets | undefined,
          type: 'input' | 'select' | 'textarea',
          disabled: boolean,
          getSelectOptions: () => {
            [key: string]: string;
          },
          searchCallback?: (searchTerm: string) => void,
        ) {
          switch (type) {
            case 'select':
              return (
                <Select
                  variant="standard"
                  options={getSelectOptions()}
                  key={id}
                  name={id}
                  value={values[id]}
                  placeholder=""
                  disabled={disabled}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  noOrdering
                  error={errors[id] && touched[id] ? errors[id] : null}
                  searchCallback={searchCallback}
                  disableFiltering={!!searchCallback}
                />
              );
            case 'input':
              return (
                <TextField
                  preset={preset}
                  key={id}
                  name={id}
                  value={values[id]}
                  placeholder={placeholder || ''}
                  variant="standard"
                  disabled={disabled}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  error={errors[id] && touched[id] ? errors[id] : null}
                />
              );
            default:
              return (
                <vp.TextArea
                  key={id}
                  name={id}
                  value={values[id]}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  maxLength={5000}
                />
              );
          }
        }

        function getReviewTableProps(values: vp.PartnerAccountTransferRequest): vp.TableProps {
          const accountTransferPair = [
            {
              display: t('transferTypeIDLabel', { defaultValue: 'Transfer Type' }),
              value: (
                <TextField
                  name="transfer-type-confirmation"
                  variant="background"
                  placeholder={transferFundOptionsLookup[values.TransferTypeID] || ''}
                  placeholderColor
                  disabled
                  onBlur={() => {}}
                  onChange={() => {}}
                />
              ),
            },
            {
              display: t('debitorAccountIDLabel', { defaultValue: 'Debitor Account (From)' }),
              value: (
                <TextField
                  name="debitor-confirmation"
                  variant="background"
                  placeholder={fromAccountDetails[values.DebitorAccountID] || ''}
                  placeholderColor
                  disabled
                  onBlur={() => {}}
                  onChange={() => {}}
                />
              ),
            },
            {
              display: t('recipientAccountIDLabel', { defaultValue: 'Recipient Account (To)' }),
              value: (
                <TextField
                  name="recipient-confirmation"
                  variant="background"
                  placeholder={toAccountDetails[values.RecipientAccountID] || ''}
                  placeholderColor
                  disabled
                  onBlur={() => {}}
                  onChange={() => {}}
                />
              ),
            },
            {
              display: t('currencyLabel', { defaultValue: 'Currency' }),
              value: (
                <TextField
                  name="currency-confirmation"
                  variant="background"
                  placeholder={values.Currency || ''}
                  placeholderColor
                  disabled
                  onBlur={() => {}}
                  onChange={() => {}}
                />
              ),
            },
            {
              display: t('amountLabel', { defaultValue: 'Amount' }),
              value: (
                <TextField
                  name="amount-confirmation"
                  variant="background"
                  placeholder={values.Amount || ''}
                  placeholderColor
                  disabled
                  onBlur={() => {}}
                  onChange={() => {}}
                />
              ),
            },
            {
              display: t('clientReferenceNumberLabel', { defaultValue: 'Client Reference Number' }),
              value: (
                <TextField
                  name="client-reference-number-confirmation"
                  variant="background"
                  placeholder={values.ClientReferenceNumber || ''}
                  placeholderColor
                  disabled
                  onBlur={() => {}}
                  onChange={() => {}}
                />
              ),
            },
            {
              display: t('notesLabel', { defaultValue: 'Notes' }),
              value: (
                <TextArea
                  name="notes-confirmation"
                  variant="background"
                  placeholder={values.Notes || ''}
                  disabled
                  onBlur={() => {}}
                  onChange={() => {}}
                />
              ),
            },
          ];

          return {
            name: 'account-transfer-details',
            variant: 'pair',
            columns: {
              detailKey: { header: 'Account Transfer' },
              detailValue: { header: '' },
            },
            rows: accountTransferPair.map(({ display, value }) => ({
              detailKey: display,
              detailValue: value,
            })),
          };
        }
      }}
    </Formik>
  );

  function createdValidationSchema() {
    return Yup.object().shape({
      TransferTypeID: Yup.string()
        .label(t('transferTypeIDLabel', { defaultValue: 'Transfer Type' }))
        .required(),
      DebitorAccountID: Yup.string()
        .label(t('debitorAccountIDLabel', { defaultValue: 'Debitor Account (From)' }))
        .required(),
      RecipientAccountID: Yup.string()
        .test(
          'same-account-validation',
          t('sameAccountValidation', { defaultValue: 'Invalid account. Debitor and Recipient cannot be the same.' }),
          (value, accountTransfer) => {
            let debitAccount = '';
            const isObjectWithDebitorAccountID = (obj: unknown): obj is { DebitorAccountID: string } =>
              typeof obj === 'object' && obj !== null && 'DebitorAccountID' in obj;

            if (isObjectWithDebitorAccountID(accountTransfer.parent)) {
              if (accountTransfer.parent.DebitorAccountID) {
                const debitAccountDetails = accountTransfer.parent.DebitorAccountID.split('-');
                debitAccount = debitAccountDetails[0] ?? '';
              }
            }

            const sameAccountValidation = value !== '' && value === debitAccount;
            return !sameAccountValidation;
          },
        )
        .label(t('recipientAccountIDLabel', { defaultValue: 'Recipient Account (To)' }))
        .required(),
      Amount: Yup.string()
        .test(
          'amount-less-than-balance-validation',
          t('amountLessThanBalanceValidation', {
            defaultValue: 'Transfer amount is too high. Please input a transfer amount below the max debitor balance',
          }),
          (value, accountTransfer) => {
            const isObjectWithTransferTypeID = (obj: unknown): obj is { TransferTypeID: string } =>
              typeof obj === 'object' && obj !== null && 'TransferTypeID' in obj;

            // If the transfer type is fund we dont need to check that the debitor accounts balance is greater than the transfer amount
            if (isObjectWithTransferTypeID(accountTransfer.parent)) {
              if (
                accountTransfer.parent.TransferTypeID === 'FundTransferAndWithdraw' ||
                accountTransfer.parent.TransferTypeID === 'FundAndTransfer'
              ) {
                return true;
              }
            }

            const isObjectWithDebitorAccountID = (obj: unknown): obj is { DebitorAccountID: string } =>
              typeof obj === 'object' && obj !== null && 'DebitorAccountID' in obj;

            let accountBalance = 0;
            const userAmount = parseFloat(value ?? '0');
            if (isObjectWithDebitorAccountID(accountTransfer.parent)) {
              if (
                accountTransfer.parent.DebitorAccountID &&
                fromAccountDetails[accountTransfer.parent.DebitorAccountID]
              ) {
                const debitAccountDetails = fromAccountDetails[accountTransfer.parent.DebitorAccountID].split('$');
                accountBalance = parseFloat(debitAccountDetails[1]);
              }
            }

            const amountBalanceValidation = userAmount > 0 && userAmount > accountBalance;
            return !amountBalanceValidation;
          },
        )
        .test(
          'invalid-amount-validation',
          t('invalidAmountValidation', { defaultValue: 'Invalid amount. Please enter a valid amount.' }),
          (value) => {
            let invalidAmountValidation = true;
            if (value) {
              const userAmount = parseFloat(value ?? '0');
              invalidAmountValidation = userAmount <= 0;
            }

            return !invalidAmountValidation;
          },
        )
        .label(t('amountLabel', { defaultValue: 'Amount' }))
        .required(),
      Currency: Yup.string()
        .test(
          'same-currency-validation',
          t('invalidCurrencyValidation', { defaultValue: 'Invalid currency. Please select the valid currency.' }),
          (value, accountTransfer) => {
            let currency = '';
            const isObjectWithDebitorAccountID = (obj: unknown): obj is { DebitorAccountID: string } =>
              typeof obj === 'object' && obj !== null && 'DebitorAccountID' in obj;

            if (isObjectWithDebitorAccountID(accountTransfer.parent)) {
              if (accountTransfer.parent.DebitorAccountID) {
                const debitAccountDetails = accountTransfer.parent.DebitorAccountID.split('-');
                currency = debitAccountDetails[1] ?? '';
              }
            }

            const sameCurrencyValidation = value !== '' && value !== currency;
            return !sameCurrencyValidation;
          },
        )
        .label(t('currencyLabel', { defaultValue: 'Currency' }))
        .required(),
    });
  }

  function getRequiredIcon(isRequired: boolean): React.JSX.Element {
    return isRequired ? <vp.Icon icon="asterisk" size="xsm" color="error" /> : <></>;
  }
};

export default PartnerAccountTransfer;
